import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '../lib/utils';
import type { Notification } from '../types/database';

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      
      // Set up subscription for real-time updates
      const notificationsChannel = supabase
        .channel('notifications-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            fetchNotifications();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(notificationsChannel);
      };
    }
  }, [user]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Use explicit foreign key names to avoid ambiguity
      const { data, error } = await supabase
        .from('notifications')
        .select(`
          *,
          actor:profiles!notifications_actor_id_fkey(id, full_name, avatar_url),
          post:posts!notifications_post_id_fkey(id, description),
          comment:comments!notifications_comment_id_fkey(id, content)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching notifications:', error);
        return;
      }

      setNotifications(data || []);
      setUnreadCount(data?.filter(n => !n.is_read).length || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId?: string) => {
    if (!user) return;

    try {
      if (notificationId) {
        // Mark single notification as read
        await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('id', notificationId)
          .eq('user_id', user.id);
          
        // Update local state
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } else if (notifications.some(n => !n.is_read)) {
        // Mark all notifications as read
        await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('user_id', user.id)
          .eq('is_read', false);
          
        // Update local state
        setNotifications(prev => 
          prev.map(n => ({ ...n, is_read: true }))
        );
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Error marking notifications as read:', err);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    if (!user) return;

    try {
      // Delete the notification from the database
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', user.id);
        
      if (error) {
        console.error('Error deleting notification:', error);
        return;
      }
        
      // Update local state by removing the notification
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      // Update unread count if the notification was unread
      const wasUnread = notifications.find(n => n.id === notificationId && !n.is_read);
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Delete the notification
    await deleteNotification(notification.id);
    
    // Navigate based on notification type
    if (notification.type === 'like' || notification.type === 'comment') {
      // Store the post ID in sessionStorage to scroll to it on the feed page
      if (notification.post_id) {
        // Clear any existing scroll targets first
        sessionStorage.removeItem('scrollToPostId');
        sessionStorage.removeItem('openCommentId');
        
        // Set new scroll targets
        sessionStorage.setItem('scrollToPostId', notification.post_id);
        
        // If it's a comment notification, also store the comment ID
        if (notification.type === 'comment' && notification.comment_id) {
          sessionStorage.setItem('openCommentId', notification.comment_id);
        }
        
        // Force a reload if already on the feed page to ensure the scroll happens
        if (window.location.pathname === '/') {
          window.location.href = '/';
        } else {
          navigate('/');
        }
      }
    } else if (notification.type === 'friend_request' || notification.type === 'friend_accept') {
      navigate('/friends');
    }
    
    setIsOpen(false);
  };

  const clearAllNotifications = async () => {
    if (!user || notifications.length === 0 || clearingAll) return;

    try {
      setClearingAll(true);

      // Delete all notifications for the user from the database
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', user.id);
        
      if (error) {
        console.error('Error clearing notifications:', error);
        return;
      }
        
      // Update local state
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Error clearing notifications:', err);
    } finally {
      setClearingAll(false);
    }
  };

  const getAvatarUrl = (profile: any) => {
    if (!profile) return `https://api.dicebear.com/7.x/initials/svg?seed=anonymous`;

    if (profile.avatar_url) {
      if (profile.avatar_url.startsWith('http')) {
        return profile.avatar_url;
      }
      const { data } = supabase.storage.from('avatars').getPublicUrl(profile.avatar_url);
      return data.publicUrl;
    }

    return `https://api.dicebear.com/7.x/initials/svg?seed=${profile.full_name}`;
  };

  const getNotificationText = (notification: Notification) => {
    switch (notification.type) {
      case 'like':
        return 'liked your post';
      case 'comment':
        return `commented: "${notification.comment?.content?.substring(0, 30)}${notification.comment?.content && notification.comment.content.length > 30 ? '...' : ''}"`;
      case 'friend_request':
        return 'sent you a friend request';
      case 'friend_accept':
        return 'accepted your friend request';
      default:
        return 'interacted with you';
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen && unreadCount > 0) {
            // Mark all as read when opening
            markAsRead();
          }
        }}
        className="p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 relative"
      >
        <Bell className="h-6 w-6 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg z-50 max-h-[80vh] overflow-y-auto">
          <div className="py-2 px-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Notifications</h3>
            <div className="flex space-x-2">
              {notifications.length > 0 && (
                <button
                  onClick={clearAllNotifications}
                  disabled={clearingAll}
                  className={cn(
                    "text-sm text-red-600 hover:text-red-800",
                    clearingAll && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {clearingAll ? 'Clearing...' : 'Clear all'}
                </button>
              )}
              {notifications.some(n => !n.is_read) && (
                <button
                  onClick={() => markAsRead()}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Mark all as read
                </button>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="py-4 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
            </div>
          ) : notifications.length > 0 ? (
            <div>
              {notifications.map(notification => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    "px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 flex items-start space-x-3",
                    !notification.is_read && "bg-blue-50"
                  )}
                >
                  <img
                    src={getAvatarUrl(notification.actor)}
                    alt={notification.actor?.full_name || 'User'}
                    className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${notification.actor?.full_name || 'anonymous'}`;
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">{notification.actor?.full_name}</span>{' '}
                      {getNotificationText(notification)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  {!notification.is_read && (
                    <span className="h-2 w-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-6 text-center text-gray-500">
              No notifications yet
            </div>
          )}
        </div>
      )}
    </div>
  );
}